import React, { useEffect, useState } from "react";
import { fetch } from "../../utils";
import { Table } from "ka-table";
import "ka-table/style.css";
import { PagingPosition, SortingMode } from "ka-table/enums";
import { Breadcrumb, Button, Card, Form, Image, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import ProdcutImagesUpdate from "./ProdcutImagesUpdate";

const childComponents = {
  table: { elementAttributes: () => ({ className: "table table-striped table-hover table-bordered" }) },
  tableHead: { elementAttributes: () => ({ className: "thead-dark" }) },
  noDataRow: { content: () => "No Data Found" }
};

const AdminProducts = () => {
  const [products, setProducts] = useState({ list: [], loading: false });
  const [searchText, setSearchText] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [priceSizeData, setPriceSizeData] = useState([]);
  const [attributesData, setAttributesData] = useState([]);
  const [editAttributesData, setEditAttributesData] = useState([]);
  const [gstList, setGstList] = useState([]);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const handleError = (error) => alert(error?.response?.data?.message || error?.message || "Something went wrong.");

  const getProductList = async () => {
    setProducts((prev) => ({ ...prev, loading: true }));
    try {
      const response = await fetch("product/all", "GET", null, null);
      const { success, data } = response.data;
      if (!success) {
        setProducts({ list: [], loading: false });
        return;
      }
      const list = (data.product_list || []).map((p) => ({
        product_id: p.product_id,
        product_name: p.product_name,
        product_name_disp: p.product_name_disp,
        product_description: p.product_description,
        category_id: p.category_id,
        sub_category_id: p.sub_category_id,
        product_code: p.product_code,
        in_stock_status: p.in_stock_status,
        product_sku: p.product_sku,
        gst_id: p.gst_id,
        gst_percent: p.gst_percent,
        images: p.images,
        product_thumbnail: { url: p.product_thumbnail, id: p.product_id },
        category_name: p.category_name,
        sub_category_name: p.sub_category_name,
        product_MRP: p.product_MRP,
        product_MSP: p.product_MSP,
        attributes: p.attributes || [],
        prices: p.prices || [],
        in_stock_status_data: { product_id: p.product_id, in_stock_status: p.in_stock_status },
        display_status: { product_id: p.product_id, display_status: p.display_status },
        desktop_display: { product_id: p.product_id, desktop_display: p.desktop_display },
        action: p.action
      }));
      setProducts({ list, loading: false });
    } catch (error) {
      setProducts({ list: [], loading: false });
      handleError(error);
    }
  };

  const getGstList = async () => {
    try {
      const response = await fetch("gst/all", "GET", null, null);
      if (response.status === 200 && response.data.success) {
        const list = response.data.data?.gst_list || [];
        setGstList(list.filter((item) => item.display_status === true));
      }
    } catch (error) {
      console.log("GST Error:", error);
    }
  };

  useEffect(() => {
    getProductList();
    getGstList();
  }, []);

  const getSubAttriByCatCategory = async (sub_category_id) => {
    try {
      const response = await fetch("attribute/get_attribute_by_sub_category_id", "POST", { sub_category_id }, null);
      if (response.status === 200 && response.data.success) setAttributesData(response.data.data.attributesWithValues || []);
    } catch (error) {
      console.log("Attribute Error:", error);
    }
  };

  const getEditAttributes = async (sub_category_id, productAttributes = []) => {
    try {
      const response = await fetch("attribute/get_attribute_by_sub_category_id", "POST", { sub_category_id }, null);
      if (response.status !== 200 || !response.data.success) {
        setEditAttributesData([]);
        return;
      }
      const apiAttributes = response.data.data.attributesWithValues || [];
      const result = apiAttributes.filter((attribute) => Number(attribute.attribute_id) !== 2).map((attribute) => {
        const selected = productAttributes.find((item) => String(item.attribute_id) === String(attribute.attribute_id));
        return {
          ...attribute,
          selected_value_id: selected?.attribute_value_id || "",
          selected_value: selected?.attribute_value || ""
        };
      });
      setEditAttributesData(result);
    } catch (error) {
      console.log("Edit Attributes Error:", error);
      setEditAttributesData([]);
    }
  };

  const updateStatus = async (url, product_id, field, value) => {
    try {
      const response = await fetch(url, "POST", {
        product_id,
        [field]: value ? 1 : 0
      }, null);

      if (response.status === 200 && response.data.success) {
        await getProductList();
      } else {
        handleError(response.data?.message || "Unable to update status.");
      }
    } catch (error) {
      handleError(error);
    }
  };

  const setProductInStockStatus = (id, value) => updateStatus("product/update-in-stock-status", id, "in_stock_status", value);
  const setProductDispalyStatus = (id, value) => updateStatus("product/update-display-status", id, "display_status", value);
  const setProductDesktopDisplay = (id, value) => updateStatus("product/update-desktop-display", id, "desktop_display", value);

  const openPriceModal = async (product) => {
    setSelectedProduct(product);
    setPriceSizeData((product.prices || []).map((item) => ({
      attribute_value_id: item.attribute_value_id,
      product_MRP: item.product_MRP,
      product_MSP: item.product_MSP
    })));
    await getSubAttriByCatCategory(product.sub_category_id);
    setShowPriceModal(true);
  };

  const addPriceSizeEntry = () => setPriceSizeData((prev) => [...prev, { attribute_value_id: "", product_MRP: "", product_MSP: "" }]);
  const removePriceSizeEntry = (index) => setPriceSizeData((prev) => prev.filter((_, i) => i !== index));
  const updatePriceField = (index, field, value) => setPriceSizeData((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));

  const updateProductPrices = async () => {
    if (!selectedProduct) return;
    try {
      const response = await fetch("product/update-product-prices", "POST", { product_id: selectedProduct.product_id, prices: priceSizeData }, null);
      if (response.status === 200 && response.data.success) {
        alert(response.data.message || "Price Updated Successfully");
        setShowPriceModal(false);
        await getProductList();
      } else alert(response.data?.message || "Unable to update price");
    } catch (error) {
      handleError(error);
    }
  };

  const handleThumbnailClick = (id) => {
    setSelectedProductId(id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProductId(null);
  };

  const openEditModal = async (product) => {
    setEditProduct({
      product_id: product.product_id,
      category_id: product.category_id || "",
      category_name: product.category_name || "",
      sub_category_id: product.sub_category_id || "",
      sub_category_name: product.sub_category_name || "",
      product_name: product.product_name || "",
      product_name_disp: product.product_name_disp || "",
      product_code: product.product_code || "",
      in_stock_status: product.in_stock_status || "",
      product_sku: product.product_sku || "",
      gst_id: product.gst_id || "",
      gst_percent: product.gst_percent || "",
      product_description: product.product_description || ""
    });
    setShowEditModal(true);
    await getEditAttributes(product.sub_category_id, product.attributes || []);
  };

  const closeEditModal = () => {
    if (editLoading) return;
    setShowEditModal(false);
    setEditProduct(null);
    setEditAttributesData([]);
  };

  const updateEditField = (field, value) => setEditProduct((prev) => ({ ...prev, [field]: value }));

  const updateEditAttribute = (index, valueId) => {
    setEditAttributesData((prev) => prev.map((item, i) => {
      if (i !== index) return item;
      const selectedValue = item.attributeValues?.find((value) => String(value.attribute_value_id) === String(valueId));
      return { ...item, selected_value_id: valueId, selected_value: selectedValue?.attribute_value || "" };
    }));
  };

  const saveProductEdit = async () => {
    if (!editProduct) return;
    if (!editProduct.product_name?.trim()) {
      alert("Please enter product name");
      return;
    }

    const attributes = editAttributesData.map((item) => ({
      attribute_id: item.attribute_id,
      attribute_value_id: item.selected_value_id,
      attribute_value: item.selected_value
    }));

    try {
      setEditLoading(true);
      const response = await fetch("product/update-product", "POST", {
        product_id: editProduct.product_id,
        product_name: editProduct.product_name,
        product_name_disp: editProduct.product_name_disp,
        product_code: editProduct.product_code,
        in_stock_status: editProduct.in_stock_status,
        product_sku: editProduct.product_sku,
        gst_id: editProduct.gst_id,
        product_description: editProduct.product_description,
        attributes
      }, null);

      if (response.status === 200 && response.data.success) {
        alert(response.data.message || "Product updated successfully");
        setShowEditModal(false);
        setEditProduct(null);
        setEditAttributesData([]);
        await getProductList();
      } else alert(response.data?.message || "Unable to update product");
    } catch (error) {
      handleError(error);
    } finally {
      setEditLoading(false);
    }
  };

  const renderSwitch = (value, type, handler) => (
    <Form.Check
      type="switch"
      checked={type === "stock" ? value === "In Stock" : !!value}
      onChange={() => handler(type === "stock" ? value !== "In Stock" : !value)}
    />
  );

  const columns = [
    { key: "product_thumbnail", title: "Image", width: "8%" },
    { key: "product_name", title: "Product Name", width: "14%" },
    { key: "product_code", title: "Code", width: "6%" },
    { key: "category_name", title: "Category", width: "9%" },
    { key: "sub_category_name", title: "Sub Category", width: "11%" },
    { key: "attributes", title: "Attributes", width: "14%" },
    { key: "prices", title: "Size / Price", width: "18%" },
    { key: "in_stock_status_data", title: "In-Stock", width: "9%", style: { textAlign: "center" } },
    { key: "display_status", title: "Active", width: "9%", style: { textAlign: "center" } },
    { key: "desktop_display", title: "Best Seller", width: "9%", style: { textAlign: "center" } },
    { key: "action", title: "Action", width: "8%", style: { textAlign: "center" } }
  ];

  return (
    <div className="container-fluid">
      <Modal show={showPriceModal} onHide={() => setShowPriceModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Manage Size & Price</Modal.Title></Modal.Header>
        <Modal.Body>
          {attributesData.filter((attribute) => Number(attribute.attribute_id) === 2).map((attribute) => (
            <div key={attribute.attribute_id}>
              <table className="table table-bordered">
                <thead><tr><th>Size</th><th>MRP</th><th>MSP</th><th></th></tr></thead>
                <tbody>
                  {priceSizeData.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <Form.Select value={item.attribute_value_id} onChange={(e) => updatePriceField(index, "attribute_value_id", e.target.value)}>
                          <option value="">Select Size</option>
                          {attribute.attributeValues?.map((att) => <option key={att.attribute_value_id} value={att.attribute_value_id}>{att.attribute_value}</option>)}
                        </Form.Select>
                      </td>
                      <td><Form.Control type="number" value={item.product_MRP} onChange={(e) => updatePriceField(index, "product_MRP", e.target.value)} /></td>
                      <td><Form.Control type="number" value={item.product_MSP} onChange={(e) => updatePriceField(index, "product_MSP", e.target.value)} /></td>
                      <td><Button variant="danger" size="sm" onClick={() => removePriceSizeEntry(index)}>X</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-end"><Button variant="dark" onClick={addPriceSizeEntry}>Add Size</Button></div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPriceModal(false)}>Close</Button>
          <Button variant="primary" onClick={updateProductPrices}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={closeEditModal} size="lg">
        <Modal.Header closeButton={!editLoading}><Modal.Title>Edit Product</Modal.Title></Modal.Header>
        <Modal.Body>
          {editProduct && (
            <div className="row">
              <Form.Group className="col-sm-6 mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select value={editProduct.category_id} disabled>
                  <option value="">-- Select --</option>
                  <option value={editProduct.category_id}>{editProduct.category_name}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="col-sm-6 mb-3">
                <Form.Label>Sub Category</Form.Label>
                <Form.Select value={editProduct.sub_category_id} disabled>
                  <option value="">-- Select --</option>
                  <option value={editProduct.sub_category_id}>{editProduct.sub_category_name}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="col-sm-6 mb-3">
                <Form.Label>Product Name</Form.Label>
                <Form.Control type="text" value={editProduct.product_name} onChange={(e) => updateEditField("product_name", e.target.value)} />
              </Form.Group>

              <Form.Group className="col-sm-6 mb-3">
                <Form.Label>Display Name</Form.Label>
                <Form.Control type="text" value={editProduct.product_name_disp} onChange={(e) => updateEditField("product_name_disp", e.target.value)} />
              </Form.Group>

              <Form.Group className="col-sm-6 mb-3">
                <Form.Label>Product Code</Form.Label>
                <Form.Control type="text" value={editProduct.product_code} onChange={(e) => updateEditField("product_code", e.target.value)} />
              </Form.Group>

              <Form.Group className="col-sm-6 mb-3">
                <Form.Label>Product SKU</Form.Label>
                <Form.Control type="text" value={editProduct.product_sku} onChange={(e) => updateEditField("product_sku", e.target.value)} />
              </Form.Group>

              <Form.Group className="col-sm-6 mb-3">
                <Form.Label>Stock</Form.Label>
                <Form.Select value={editProduct.in_stock_status} onChange={(e) => updateEditField("in_stock_status", e.target.value)}>
                  <option value="">-- Select --</option>
                  <option value="In Stock">In Stock</option>
                  <option value="Out Of Stock">Out Of Stock</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="col-sm-6 mb-3">
                <Form.Label>GST</Form.Label>
                <Form.Select value={editProduct.gst_id} onChange={(e) => updateEditField("gst_id", e.target.value)}>
                  <option value="">-- Select GST --</option>
                  {gstList.map((gst) => <option key={gst.gst_id} value={gst.gst_id}>{gst.gst_percent}%</option>)}
                </Form.Select>
              </Form.Group>

              <Form.Group className="col-sm-12 mb-3">
                <Form.Label>Attributes</Form.Label>
                {editAttributesData.length ? (
                  <div className="border rounded p-3">
                    <div className="row">
                      {editAttributesData.map((attribute, index) => (
                        <div className="col-sm-6 mb-3" key={attribute.attribute_id || index}>
                          <Form.Label>{attribute.attribute_name}</Form.Label>
                          <Form.Select value={attribute.selected_value_id || ""} onChange={(e) => updateEditAttribute(index, e.target.value)}>
                            <option value="">Select {attribute.attribute_name}</option>
                            {(attribute.attributeValues || []).map((value) => <option key={value.attribute_value_id} value={value.attribute_value_id}>{value.attribute_value}</option>)}
                          </Form.Select>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <div className="border rounded p-3 text-muted">Loading attributes...</div>}
              </Form.Group>

              <Form.Group className="col-sm-12 mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={4} value={editProduct.product_description} onChange={(e) => updateEditField("product_description", e.target.value)} />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal} disabled={editLoading}>Cancel</Button>
          <Button variant="primary" onClick={saveProductEdit} disabled={editLoading}>{editLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </Modal>

      <Breadcrumb className="p-2">
        <Breadcrumb.Item href="#">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item active>Products</Breadcrumb.Item>
      </Breadcrumb>

      <section className="content">
        <div className="container-fluid">
          <Card>
            <Card.Header>
              <h4>Products <Link to="/admin/products/add" className="btn btn-xs btn-primary pull-right">Add New Product</Link></h4>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-sm-12"><input type="search" value={searchText} onChange={(e) => setSearchText(e.target.value)} className="top-element pull-right m-2" placeholder="Search..." /></div>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <Table
                    striped
                    bordered
                    hover
                    loading={{ enabled: products.loading, text: "Loading data" }}
                    columns={columns}
                    data={products.list}
                    searchText={searchText}
                    paging={{ enabled: true, pageIndex: 0, pageSize: 10, pageSizes: [5, 10, 15], position: PagingPosition.Bottom }}
                    rowKeyField="product_id"
                    noData={{ text: "No Data Found" }}
                    sortingMode={SortingMode.Single}
                    childComponents={{
                      ...childComponents,
                      cell: {
                        content: ({ column, rowData, value }) => {
                          if (column.key === "product_thumbnail") return <div onClick={() => handleThumbnailClick(value.id)}><Image src={value.url} thumbnail style={{ maxWidth: "70%", cursor: "pointer" }} alt={value.id} /></div>;

                          if (column.key === "attributes") return (
                            <table className="table table-bordered table-sm mb-0">
                              <tbody>{value?.map((attr, index) => <tr key={index}><td><b>{attr.attribute_name}</b></td><td>{attr.attribute_value}</td></tr>)}</tbody>
                            </table>
                          );

                          if (column.key === "prices") return (
                            <div style={{ cursor: "pointer" }} onClick={() => openPriceModal(rowData)}>
                              <table className="table table-bordered table-sm mb-0">
                                <thead style={{ fontSize: "12px" }}><tr><th>Size <i className="fa fa-edit text-primary" /></th><th>MRP</th><th>MSP</th></tr></thead>
                                <tbody>{value?.map((price, index) => <tr key={index}><td>{price.attribute_value}</td><td>₹{price.product_MRP}</td><td>₹{price.product_MSP}</td></tr>)}</tbody>
                              </table>
                            </div>
                          );

                          if (column.key === "in_stock_status_data") return (
                            <>
                              <p className={`text-xs fw-bold m-0 ${value.in_stock_status === "In Stock" ? "text-success" : "text-danger"}`} style={{ fontSize: "12px" }}>({value.in_stock_status})</p>
                              {renderSwitch(value.in_stock_status, "stock", (v) => setProductInStockStatus(value.product_id, v))}
                            </>
                          );

                          if (column.key === "display_status") return renderSwitch(value.display_status, "status", (v) => setProductDispalyStatus(value.product_id, v));
                          if (column.key === "desktop_display") return renderSwitch(value.desktop_display, "status", (v) => setProductDesktopDisplay(value.product_id, v));
                          if (column.key === "action") return <Button variant="primary" size="sm" onClick={() => openEditModal(rowData)}><i className="fa fa-edit me-1" />Edit</Button>;

                          return value;
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </section>

      <ProdcutImagesUpdate show={showModal} onClose={closeModal} selectedProduct={selectedProductId} products={products.list} getProductList={getProductList} />
    </div>
  );
};

export default AdminProducts;